import { useReducer, useState, useCallback, useEffect, useRef } from "react";
import { convAiClient } from "../API";
export const transformFilterOptions = (options: any[]) => {
  if (!options) return [];
  return options.map((option) => ({
    label: String(option.id),
    value: String(option.value),
  }));
};

export interface FilterItem {
  name: string;
  placeholder: string;
  value: string | null;
  options: { id: string; value: string }[];
  is_multi_value: boolean;
}

export interface State {
  filters: FilterItem[];
  title: string;
  currentLocation: {
    formattedAddress: string;
    location: {
      lat: number;
      lng: number;
    } | null;
  } | null;
  preferredTransportMode: "DRIVING" | "TRANSIT" | "WALKING" | "CYCLING";
  commuteTime: number;
  jobList: any[];
  page: {
    size: number;
    page: number;
    total_pages: number;
  };
  shouldFetch?: boolean;
  isLoadingMore?: boolean;
  bounding_box: {
    northeast: {
      lat: number;
      lng: number;
    };
    southwest: {
      lat: number;
      lng: number;
    };
  } | null;
}

export type Action = {
  type: string;
  payload?: {
    key?: string;
    value?: any;
    page?: any;
  };
};

const initialState: State = {
  filters: [],

  currentLocation: null,
  preferredTransportMode: "DRIVING",
  commuteTime: 60,
  jobList: [],
  title: "",
  page: { size: 10, page: 1, total_pages: 1 },
  shouldFetch: false,
  isLoadingMore: false,
  bounding_box: null,
};

function reducer(state: State, action: Action): State {
  const { payload } = action || {};
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        filters: state.filters.map((f) =>
          f.name === payload?.key ? { ...f, value: payload?.value } : f
        ),
        shouldFetch: true,
      };

    case "SET_FILTERS":
      return {
        ...state,
        filters: payload?.value || [],
        shouldFetch: true,
      };

    case "RESET_FILTERS":
      return {
        ...state,
        filters: payload?.value || [],
        shouldFetch: true,
      };

    case "SET_CURRENT_LOCATION":
      return { ...state, currentLocation: payload?.value, shouldFetch: true };
    case "SET_PREFERRED_TRANSPORT_MODE":
      return {
        ...state,
        preferredTransportMode: payload?.value,
        shouldFetch: true,
      };
    case "SET_COMMUTE_TIME":
      return { ...state, commuteTime: payload?.value, shouldFetch: true };
    case "SET_JOB_LIST":
      return { ...state, jobList: payload?.value || [] };
    case "APPEND_JOB_LIST":
      return {
        ...state,
        jobList: [...state.jobList, ...(payload?.value || [])],
        page: payload?.page ? payload.page : state.page,
        isLoadingMore: false,
      };
    case "SET_LOADING_MORE":
      return { ...state, isLoadingMore: payload?.value };
    case "SET_INITIAL_STATE":
      return {
        ...state,
        ...payload?.value,
        shouldFetch: false,
        isLoadingMore: false,
      };
    case "FETCH_COMPLETE":
      return { ...state, shouldFetch: false, isLoadingMore: false };
    case "RESET":
      return { ...initialState };
    default:
      return state;
  }
}
const transformToPayload = (state: State) => {
  const {
    filters,
    currentLocation,
    preferredTransportMode,
    commuteTime,
    page,
  } = state || {};
  return {
    filters,
    page,
    location: {
      address: currentLocation?.formattedAddress || "",
      coordinates: currentLocation?.location || null,
      commute: {
        mode: preferredTransportMode,
        time: commuteTime,
      },
    },
  };
};
export function useJobListMap(botId: string, convId: string) {
  // console.log("#### useJobListMap    ", { botId, convId });
  const [state, dispatch] = useReducer(reducer, initialState);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchMoreAbortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(
    async ({ payload: overridePayload }: { payload: any }) => {
      // Cancel previous request if it exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoading(true);
      try {
        const payload = overridePayload;
        const response = await convAiClient.post(
          `/v1/bots/${botId}/conversations/${convId}/job_search`,
          payload,
          {
            signal: abortController.signal,
          }
        );

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        const {
          filters,
          jobs,
          location,
          page,
          view_mode,
          available_view_modes,
          title,
          bounding_box,
        } = response?.data || {};

        const { address, commute, coordinates } = location || {};

        const { mode = "DRIVING", time = 60 } = commute || {};

        dispatch({
          type: "SET_INITIAL_STATE",
          payload: {
            value: {
              currentLocation: {
                formattedAddress: address,
                location: coordinates,
              },
              preferredTransportMode: mode,
              commuteTime: time,
              jobList: jobs,
              filters: filters,
              title: title,
              page: page, //{ ...page, total_pages: 10 },
              bounding_box: bounding_box,
            },
          },
        });

        // Return view mode data for provider to use
        return { view_mode, available_view_modes, filters };
      } catch (error: any) {
        // Don't handle aborted requests as errors
        if (error.name === "AbortError" || abortController.signal.aborted) {
          return;
        }
        throw error;
      } finally {
        // Only update loading state if this request wasn't aborted
        if (!abortController.signal.aborted) {
          setLoading(false);
          dispatch({ type: "FETCH_COMPLETE" });
        }
        // Clear the abort controller reference if this was the current one
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [botId, convId]
  );

  // Infinite scroll: fetch more jobs and append
  const fetchMoreJobs = useCallback(async () => {
    if (state.isLoadingMore) return;
    if (state.page.page >= state.page.total_pages) return;

    // Cancel previous fetchMore request if it exists
    if (fetchMoreAbortControllerRef.current) {
      fetchMoreAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    fetchMoreAbortControllerRef.current = abortController;

    dispatch({ type: "SET_LOADING_MORE", payload: { value: true } });
    try {
      const payload = transformToPayload({
        ...state,
        page: { ...state.page, page: state.page.page + 1 },
      });
      const response = await convAiClient.post(
        `/v1/bots/${botId}/conversations/${convId}/job_search`,
        payload,
        {
          signal: abortController.signal,
        }
      );

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      const { jobs, page } = response?.data || {};
      dispatch({
        type: "APPEND_JOB_LIST",
        payload: { value: jobs, page },
      });
    } catch (error: any) {
      // Don't handle aborted requests as errors
      if (error.name === "AbortError" || abortController.signal.aborted) {
        return;
      }
      dispatch({ type: "SET_LOADING_MORE", payload: { value: false } });
    } finally {
      // Clear the abort controller reference if this was the current one
      if (fetchMoreAbortControllerRef.current === abortController) {
        fetchMoreAbortControllerRef.current = null;
      }
    }
  }, [botId, convId, state]);

  // useEffect to fetch when shouldFetch is true
  useEffect(() => {
    if (state.shouldFetch) {
      const overridePayload = transformToPayload({
        ...state,
        page: { ...state.page, page: 1 },
      });
      fetchData({ payload: overridePayload });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch]);

  // Cleanup: abort any ongoing request when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (fetchMoreAbortControllerRef.current) {
        fetchMoreAbortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    state,
    dispatch,
    fetchData,
    fetchMoreJobs,
    loading,
    isLoadingMore: state.isLoadingMore,
  };
}
